import { Button } from '@fluentui/react-components';
import { ArrowUndoFilled, SaveRegular } from '@fluentui/react-icons';
import {
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBlocker, useParams } from 'react-router-dom';

import {
  FormLayout,
  FormLayoutProps,
  PathsSelectorFrom,
} from '@tdp/common/form';
import { DeveloperPortalRootContext, useCommand } from '@tdp/core';

import { IAppDetails } from '../abstractions';
import { ChangeRevertConfirmationDialog } from '../components';
import {
  createAppEditsHook,
  createAppZoomHook,
  createAppRevertHook,
} from '../hooks';

export type AppDetailsFormLayoutProps = Omit<
  FormLayoutProps,
  'footer' | 'main' | 'form'
>;

const useAppLens = createAppZoomHook((app) => app);

/**
 * Create a new AppDetails form layout
 *
 * This is a factory because the 'save' & 'revert' buttons will enable/disable based on the declaration of what paths are modified
 * on the page.
 * @param paths The paths selector for which fields are modified in the current form
 * @returns A React component for the form layout.
 */
export function createAppDetailsFormLayout(
  paths: PathsSelectorFrom<IAppDetails>
) {
  const useEditTracking = createAppEditsHook(paths);
  const useRevert = createAppRevertHook(paths);

  return function AppDetailsFormLayout(props: AppDetailsFormLayoutProps) {
    const formId = useId();
    //TODO(nibeauli): factor this out into a hook
    const appId = useParams<{ appId: string }>().appId ?? '';
    const intl = useIntl();
    const { services: { logger } } = useContext(DeveloperPortalRootContext);

    const [hasEdits] = useEditTracking(appId);
    const formLens = useAppLens(appId);
    const writeApp = useCommand(
      (commands) => commands.appDetailCommands.writeApp
    );
    const [revertConfirmDialogOpen, setRevertConfirmDialogOpen] =
      useState(false);
    const onSave: MouseEventHandler<typeof Button> = useCallback(
      (evt) => {
        logger.logUserBi({
          telemetryMarker: 'app-details-action-form-save',
          message: `App details form save button clicked with appId: ${appId}`,
          appId,
        });
        // don't let the form submit occur and trigger a navigation event
        evt.preventDefault();
        writeApp({
          appDetails: formLens.current,
        });
      },
      [writeApp, formLens.current]
    );

    const onRevert = useRevert(appId);
    const revertConfirmationHandler = useCallback(() => {
      setRevertConfirmDialogOpen(false);
      onRevert();
    }, [onRevert]);

    const dismisssRevertConfirmationDialog = useCallback(() => {
      setRevertConfirmDialogOpen(false);
    }, []);

    const openRevertConfirmationDialog = useCallback(() => {
      setRevertConfirmDialogOpen(true);
    }, []);
    //TODO(hook navigation events as well to avoid navigation out of the TDP app)
    const blockerState = useBlocker(hasEdits);
    useEffect(() => {
      if (blockerState.state === 'blocked') {
        const test = confirm(
          intl.formatMessage({
            defaultMessage:
              'You have unsaved changes, please save or discard them before navigating away.',
            description:
              'Confirmation message when user tries to navigate away with unsaved changes.',
          })
        );
        if (test) {
          blockerState.proceed();
        } else {
          blockerState.reset();
        }
      }
    }, [blockerState]);

    if (!appId) {
      return null;
    }

    const Footer = () => (
      <>
        <ChangeRevertConfirmationDialog
          onConfirm={revertConfirmationHandler}
          onCancel={dismisssRevertConfirmationDialog}
          isOpen={revertConfirmDialogOpen}
        />
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          disabled={!hasEdits}
          onClick={onSave}
          form={formId}
          type="submit"
        >
          <FormattedMessage defaultMessage="Save" />
        </Button>
        <Button
          icon={<ArrowUndoFilled />}
          disabled={!hasEdits}
          onClick={openRevertConfirmationDialog}
        >
          <FormattedMessage defaultMessage="Revert" />
        </Button>
      </>
    );

    return (
      <FormLayout
        footer={<Footer />}
        header={props.header}
        form={{
          id: formId,
        }}
        className={props.className}
      >
        {props.children}
      </FormLayout>
    );
  };
}
